import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar, Trash2 } from "lucide-react";
import ReusableCalendar from "../../components/AttendanceCalender/ReusableCalendar";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";

// Helper function to check if leave is full day
const isFullDayLeave = (leaveDuration) => {
  if (!leaveDuration) return false;
  const duration = leaveDuration.trim().toUpperCase();
  return duration === "FULL_DAY" || duration === "FULL DAY";
};

// Helper functions to interpret leave approval status
const normalizeLeaveStatus = (leaveStatus) =>
  (leaveStatus || "").trim().toUpperCase();

const isPendingLeave = (leaveStatus) => {
  const status = normalizeLeaveStatus(leaveStatus);
  return status === "REQUESTED" || status === "PENDING";
};

const isRevokedLeave = (leaveStatus) => {
  const status = normalizeLeaveStatus(leaveStatus);
  return (
    status === "REVOKED" || status === "CANCELLED" || status === "CANCELED"
  );
};

// A revoked leave no longer blocks the day — the employee is treated as
// working that day, so it should never drive attendance/leave counts.
const getEffectiveLeave = (leave) =>
  leave && !isRevokedLeave(leave.leaveStatus) ? leave : null;

// Enhanced Attendance Cell Component with Company Holidays
const AttendanceCell = ({
  record,
  dayInfo,
  onClick,
  canDelete = false,
  onDelete,
}) => {
  const getStatusColor = () => {
    // Days before the employee's date of joining aren't attendance days at
    // all — render them blank (like future dates) instead of "Absent".
    if (dayInfo?.dayType === "before-joining") {
      return {
        bg: "#ffffff",
        text: "#cbd5e1",
        border: "#f1f5f9",
        status: "",
      };
    }

    // Company holiday takes highest priority
    if (dayInfo?.isCompanyHoliday) {
      return {
        bg: "#fef3c7",
        text: "#92400e",
        border: "#fde68a",
        status: dayInfo.holidayName || "Holiday",
      };
    }

    // Then working days holiday
    if (dayInfo?.dayType === "holiday") {
      return {
        bg: "#f1f5f9",
        text: "#64748b",
        border: "#cbd5e1",
        status: "Weekly Off",
      };
    }

    // Leave requested but not yet approved/rejected
    if (dayInfo?.dayType === "leave-requested") {
      return {
        bg: "#fef3c7",
        text: "#92400e",
        border: "#fcd34d",
        status: "Leave Requested",
      };
    }

    // Leave (approved)
    if (dayInfo?.dayType === "leave") {
      const isFullDay = isFullDayLeave(dayInfo.leaveDuration);
      return {
        bg: isFullDay ? "#ffedd5" : "#fef9c3",
        text: isFullDay ? "#9a3412" : "#854d0e",
        border: isFullDay ? "#fdba74" : "#fde047",
        status: isFullDay ? "LEAVE" : "½ DAY",
      };
    }

    // Date of joining — call this out explicitly rather than letting it
    // fall through to a plain Present/Not Punched label.
    if (dayInfo?.isJoiningDay) {
      return {
        bg: "#eef2ff",
        text: "#4338ca",
        border: "#c7d2fe",
        status: "Joined",
      };
    }

    // Future date
    const cellDate = new Date(dayInfo?.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    cellDate.setHours(0, 0, 0, 0);

    if (cellDate > today) {
      return {
        bg: "#ffffff",
        text: "#94a3b8",
        border: "#f1f5f9",
        status: "",
      };
    }

    // Attendance status
    if (!record) {
      return {
        bg: "#fef2f2",
        text: "#dc2626",
        border: "#fecaca",
        status: "Not Punched",
      };
    }

    if (record.punchInTime && record.punchOutTime) {
      return {
        bg: "#f0fdf4",
        text: "#16a34a",
        border: "#bbf7d0",
        status: "Present",
      };
    }

    if (record.punchInTime && !record.punchOutTime) {
      return {
        bg: "#fffbeb",
        text: "#d97706",
        border: "#fde68a",
        status: "Partial",
      };
    }

    return {
      bg: "#fef2f2",
      text: "#dc2626",
      border: "#fecaca",
      status: "Not Punched",
    };
  };

  const formatTimeDisplay = (timeValue) => {
    if (!timeValue) return "";

    if (typeof timeValue === "object" && timeValue.hour !== undefined) {
      const hour = timeValue.hour;
      const minute = timeValue.minute;
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    }

    const match = timeValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (match) {
      const hour = parseInt(match[1]);
      const minute = match[2];
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute}${period}`;
    }
    return timeValue;
  };

  const statusColors = getStatusColor();
  const isClickable =
    record || (dayInfo?.dayType !== "future" && statusColors.status !== "");
  const hasValidTimeRecord =
    record && (record.punchInTime || record.punchOutTime);
  const showTimeSection = hasValidTimeRecord;

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={`attendance-cell ${isClickable ? "has-record" : ""}`}
      style={{
        backgroundColor: statusColors.bg,
        borderColor: statusColors.border,
      }}
      title={
        dayInfo?.isCompanyHoliday
          ? `${dayInfo.holidayName} - Company Holiday`
          : statusColors.status
      }
    >
      {canDelete && record?.attendanceId && (
        <button
          type="button"
          className="cell-delete-btn"
          title="Delete attendance record"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(record);
          }}
        >
          <Trash2 size={10} />
        </button>
      )}
      <div className="cell-status">{statusColors.status}</div>
      {dayInfo?.leaveRevoked && (
        <div className="cell-revoked-tag">Leave Revoked</div>
      )}
      {showTimeSection && (
        <>
          {record.punchInTime && record.punchInTime !== "—" && (
            <div className="cell-time">
              {formatTimeDisplay(record.punchInTime)}
            </div>
          )}
          {record.punchOutTime && record.punchOutTime !== "—" && (
            <div className="cell-time">
              {formatTimeDisplay(record.punchOutTime)}
            </div>
          )}
        </>
      )}
      {dayInfo?.isCompanyHoliday && !showTimeSection && (
        <div className="holiday-name">{dayInfo.holidayName}</div>
      )}
    </div>
  );
};

// Main Enhanced Calendar Component
function EnhancedAttendanceCalendar({
  calendarData,
  employeeList,
  onMonthChange,
  singleEmployeeData = null,
  showEmployeeSelector = false,
  canDelete = false,
  onDeleteRecord,
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all");
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Get holidays from employee data instead of fetching separately
  const companyHolidays = React.useMemo(() => {
    if (singleEmployeeData) {
      return singleEmployeeData.holidays || [];
    }
    if (!employeeList || employeeList.length === 0) return [];
    // Use holidays from the first employee (all employees in same branch should have same holidays)
    const firstEmployee = employeeList[0];
    return firstEmployee?.holidays || [];
  }, [employeeList, singleEmployeeData]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/list`,
        );

        setEmployees(res.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchEmployees();
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { daysInMonth, year, month };
  };

  const isHoliday = (date, workingDays) => {
    if (!workingDays) return false;

    const dayOfWeek = date.getDay();
    const dayName = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ][dayOfWeek];

    if (!workingDays[dayName]) return true;

    const weekOfMonth = Math.ceil(date.getDate() / 7);
    const weekNames = ["first", "second", "third", "fourth", "fifth"];
    const weekName = weekNames[weekOfMonth - 1];

    if (
      dayName === "saturday" &&
      workingDays[
        `leaveOn${weekName.charAt(0).toUpperCase() + weekName.slice(1)}Saturday`
      ]
    ) {
      return true;
    }
    if (
      dayName === "sunday" &&
      workingDays[
        `leaveOn${weekName.charAt(0).toUpperCase() + weekName.slice(1)}Sunday`
      ]
    ) {
      return true;
    }

    return false;
  };

  const isCompanyHoliday = (dateString) => {
    return companyHolidays.find((holiday) => holiday.eventDate === dateString);
  };

  const calculateHours = (punchIn, punchOut) => {
    if (!punchIn || !punchOut || punchIn === "—" || punchOut === "—") return 0;

    try {
      const parseTime = (timeVal) => {
        if (typeof timeVal === "object" && timeVal.hour !== undefined) {
          return timeVal.hour + timeVal.minute / 60;
        }

        const match = timeVal.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
        if (match) {
          const hours = parseInt(match[1]);
          const minutes = parseInt(match[2]);
          return hours + minutes / 60;
        }
        return 0;
      };

      const inTime = parseTime(punchIn);
      const outTime = parseTime(punchOut);

      let hours = outTime - inTime;
      if (hours < 0) hours += 24;

      return hours;
    } catch (e) {
      return 0;
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);

    if (onMonthChange) {
      onMonthChange(newDate, selectedEmployeeId);
    }
  };

  const { daysInMonth, year, month } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const calculateSummaryStats = () => {
    let totalPresent = 0;
    let totalPartial = 0;
    let totalAbsent = 0;
    let totalHolidays = 0;
    let totalLeaves = 0;
    let totalCompanyHolidays = 0;
    let totalHoursWorked = 0;
    let totalDaysWorked = 0;
    let totalWorkingDays = 0;

    if (
      selectedEmployeeId &&
      selectedEmployeeId !== "all" &&
      singleEmployeeData
    ) {
      const detailed = calculateEmployeeDetailedStats(singleEmployeeData);
      const basic = calculateEmployeeStats(singleEmployeeData);

      totalPresent = detailed.present;
      totalPartial = detailed.partial;
      totalAbsent = detailed.absent;
      totalHolidays = detailed.holidays;
      totalLeaves = detailed.leaves;
      totalCompanyHolidays = detailed.companyHolidays;
      totalHoursWorked = detailed.totalHours;
      totalDaysWorked = basic.daysWorked;
      totalWorkingDays = basic.totalWorkingDays;
    } else {
      employeeList.forEach((employee) => {
        if (!employee.monthlyAttendance) return;

        const detailed = calculateEmployeeDetailedStats(employee);
        const basic = calculateEmployeeStats(employee);

        totalPresent += detailed.present;
        totalPartial += detailed.partial;
        totalAbsent += detailed.absent;
        totalHolidays += detailed.holidays;
        totalLeaves += detailed.leaves;
        totalCompanyHolidays += detailed.companyHolidays;
        totalHoursWorked += detailed.totalHours;
        totalDaysWorked += basic.daysWorked;
        totalWorkingDays += basic.totalWorkingDays;
      });
    }

    const avgWorkingHours =
      totalDaysWorked > 0 ? totalHoursWorked / totalDaysWorked : 0;

    return {
      totalPresent,
      totalPartial,
      totalAbsent,
      totalHolidays,
      totalLeaves,
      totalCompanyHolidays,
      totalHoursWorked,
      avgWorkingHours,
      totalDaysWorked,
      totalWorkingDays,
    };
  };

  const calculateEmployeeDetailedStats = (employeeData) => {
    const workingDays = employeeData.workingDays;
    const attendanceMap = {};
    const leaveMap = {};

    let present = 0;
    let partial = 0;
    let absent = 0;
    let holidays = 0;
    let leaves = 0;
    let companyHolidays = 0;
    let totalHours = 0;

    employeeData.monthlyAttendance?.forEach((att) => {
      attendanceMap[att.attendanceDate] = att;
    });

    employeeData.leaveHistories?.forEach((leave) => {
      leaveMap[leave.leaveDate] = leave;
    });

    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === year && today.getMonth() === month;
    const daysToCount = isCurrentMonth ? today.getDate() : daysInMonth;

    // Days before the employee joined aren't attendance days — exclude
    // them from every stat rather than letting them count as absent.
    const dojKey = employeeData.doj ? employeeData.doj.substring(0, 10) : null;

    for (let day = 1; day <= daysToCount; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;
      const date = new Date(year, month, day);

      const cellDate = new Date(date);
      const todayDate = new Date(today);
      cellDate.setHours(0, 0, 0, 0);
      todayDate.setHours(0, 0, 0, 0);
      if (cellDate > todayDate) continue;
      if (dojKey && dateKey < dojKey) continue;

      const record = attendanceMap[dateKey];
      // A revoked leave no longer counts as leave — the employee worked
      // (or should be marked present/absent) that day as normal.
      const leave = getEffectiveLeave(leaveMap[dateKey]);
      const isHol = isHoliday(date, workingDays);
      const companyHoliday = isCompanyHoliday(dateKey);

      if (companyHoliday) {
        const isOptional = companyHoliday.holidayType === "Optional";
        if (isOptional && leave) {
          // Optional holiday but employee took leave — count as leave
          leaves++;
          if (
            !isFullDayLeave(leave.leaveDuration) &&
            record &&
            record.punchInTime
          ) {
            if (record.punchInTime && record.punchOutTime) {
              const hours = calculateHours(
                record.punchInTime,
                record.punchOutTime,
              );
              totalHours += hours;
            }
          }
        } else {
          companyHolidays++;
        }
        continue;
      }

      if (isHol) {
        holidays++;
      } else if (leave) {
        leaves++;
        if (
          !isFullDayLeave(leave.leaveDuration) &&
          record &&
          record.punchInTime
        ) {
          if (record.punchInTime && record.punchOutTime) {
            const hours = calculateHours(
              record.punchInTime,
              record.punchOutTime,
            );
            totalHours += hours;
          }
        }
      } else {
        if (record && record.punchInTime && record.punchOutTime) {
          present++;
          const hours = calculateHours(record.punchInTime, record.punchOutTime);
          totalHours += hours;
        } else if (record && record.punchInTime && !record.punchOutTime) {
          partial++;
        } else {
          absent++;
        }
      }
    }

    const avgHours = present > 0 ? totalHours / present : 0;

    return {
      present,
      partial,
      absent,
      holidays,
      leaves,
      companyHolidays,
      totalHours,
      avgHours,
    };
  };

  const calculateEmployeeStats = (employeeData) => {
    const workingDays = employeeData.workingDays;
    const attendanceMap = {};
    const leaveMap = {};

    let daysWorked = 0;
    let totalWorkingDays = 0;

    employeeData.monthlyAttendance?.forEach((att) => {
      attendanceMap[att.attendanceDate] = att;
    });

    employeeData.leaveHistories?.forEach((leave) => {
      leaveMap[leave.leaveDate] = leave;
    });

    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === year && today.getMonth() === month;
    const daysToCount = isCurrentMonth ? today.getDate() : daysInMonth;

    // Days before the employee joined aren't attendance days — exclude
    // them from every stat rather than letting them count as absent.
    const dojKey = employeeData.doj ? employeeData.doj.substring(0, 10) : null;

    for (let day = 1; day <= daysToCount; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;
      if (dojKey && dateKey < dojKey) continue;
      const date = new Date(year, month, day);
      const record = attendanceMap[dateKey];
      const leave = getEffectiveLeave(leaveMap[dateKey]);

      const isHol = isHoliday(date, workingDays);
      const companyHoliday = isCompanyHoliday(dateKey);

      if (!isHol && !companyHoliday && !leave) {
        totalWorkingDays++;
        if (record && record.punchInTime && record.punchOutTime) {
          daysWorked++;
        }
      } else if (leave && !isFullDayLeave(leave.leaveDuration)) {
        totalWorkingDays++;
        if (record && record.punchInTime && record.punchOutTime) {
          daysWorked++;
        }
      }
    }

    return { daysWorked, totalWorkingDays };
  };

  const stats = calculateSummaryStats();

  // If single employee is selected, use ReusableCalendar with stats
  if (
    selectedEmployeeId &&
    selectedEmployeeId !== "all" &&
    singleEmployeeData
  ) {
    const empStats = calculateEmployeeDetailedStats(singleEmployeeData);
    const basicStats = calculateEmployeeStats(singleEmployeeData);

    return (
      <div style={styles.container}>
        {/* Enhanced Stats cards for single employee */}
        <div style={styles.statsGrid}>
          <div style={{ ...styles.statCard, background: "#10b981" }}>
            <div style={styles.statLabel}>Present Days</div>
            <div style={styles.statValue}>{empStats.present}</div>
          </div>
          <div style={{ ...styles.statCard, background: "#f59e0b" }}>
            <div style={styles.statLabel}>Partial Days</div>
            <div style={styles.statValue}>{empStats.partial}</div>
          </div>
          <div style={{ ...styles.statCard, background: "#ef4444" }}>
            <div style={styles.statLabel}>Not Punched Days</div>
            <div style={styles.statValue}>{empStats.absent}</div>
          </div>
          <div style={{ ...styles.statCard, background: "#667eea" }}>
            <div style={styles.statLabel}>Attendance Rate</div>
            <div style={styles.statValue}>
              {basicStats.totalWorkingDays > 0
                ? Math.round(
                    (basicStats.daysWorked / basicStats.totalWorkingDays) * 100,
                  )
                : 0}
              %
            </div>
          </div>
          <div style={{ ...styles.statCard, background: "#94a3b8" }}>
            <div style={styles.statLabel}>Weekly Offs</div>
            <div style={styles.statValue}>{empStats.holidays}</div>
          </div>
          <div style={{ ...styles.statCard, background: "#fbbf24" }}>
            <div style={styles.statLabel}>Company Holidays</div>
            <div style={styles.statValue}>{empStats.companyHolidays}</div>
          </div>
          <div style={{ ...styles.statCard, background: "#fb923c" }}>
            <div style={styles.statLabel}>Leaves</div>
            <div style={styles.statValue}>{empStats.leaves}</div>
          </div>
          <div style={{ ...styles.statCard, background: "#8b5cf6" }}>
            <div style={styles.statLabel}>Total Hours</div>
            <div style={styles.statValue}>
              {Math.round(empStats.totalHours)}h
            </div>
          </div>
          <div style={{ ...styles.statCard, background: "#06b6d4" }}>
            <div style={styles.statLabel}>Avg Hours/Day</div>
            <div style={styles.statValue}>{empStats.avgHours.toFixed(1)}h</div>
          </div>
        </div>

        {/* Render the ReusableCalendar with company holidays */}
        <ReusableCalendar
          attendanceData={singleEmployeeData.monthlyAttendance || []}
          onMonthChange={(date) => {
            setCurrentMonth(date);
            if (onMonthChange) {
              onMonthChange(date, singleEmployeeData.employeeId);
            }
          }}
          workingDays={singleEmployeeData.workingDays}
          leaveHistories={singleEmployeeData.leaveHistories || []}
          employeeName={singleEmployeeData.employeeName}
          companyHolidays={companyHolidays}
        />
      </div>
    );
  }

  // Multi-employee table view
  return (
    <div style={styles.container}>
      <style>{cellStyles}</style>

      <div style={styles.monthNav}>
        <button onClick={() => changeMonth(-1)} style={styles.navButton}>
          <ChevronLeft size={20} />
        </button>
        <h3 style={styles.monthTitle}>{monthName}</h3>
        <button onClick={() => changeMonth(1)} style={styles.navButton}>
          <ChevronRight size={20} />
        </button>

        {/* Employee Selector - Only show if showEmployeeSelector is true */}
        {showEmployeeSelector && (
          <div
            style={{
              padding: "16px",
              background: "#f9fafb",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              maxWidth: "500px",
            }}
          >
            <label
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                minWidth: "120px",
                flexShrink: 0,
              }}
            >
              Select Employee:
            </label>
            <select
              value={selectedEmployeeId}
              onChange={(e) => {
                const newEmployeeId = e.target.value;
                setSelectedEmployeeId(newEmployeeId);
                if (onMonthChange) {
                  onMonthChange(currentMonth, newEmployeeId);
                }
              }}
              style={{
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                background: "#fff",
                color: "#1f2937",
                cursor: "pointer",
                minWidth: "250px",
                maxWidth: "350px",
                flex: "1",
                transition: "all 0.2s",
                outline: "none",
              }}
            >
              <option value="all">All Employees</option>
              {employees.map((emp) => (
                <option key={emp.empId} value={emp.empId}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, background: "#10b981" }}>
          <div style={styles.statLabel}>Present Days</div>
          <div style={styles.statValue}>{stats.totalPresent}</div>
        </div>
        <div style={{ ...styles.statCard, background: "#f59e0b" }}>
          <div style={styles.statLabel}>Partial Days</div>
          <div style={styles.statValue}>{stats.totalPartial}</div>
        </div>
        <div style={{ ...styles.statCard, background: "#ef4444" }}>
          <div style={styles.statLabel}>Not Punched Days</div>
          <div style={styles.statValue}>{stats.totalAbsent}</div>
        </div>
        <div style={{ ...styles.statCard, background: "#667eea" }}>
          <div style={styles.statLabel}>Attendance Rate</div>
          <div style={styles.statValue}>
            {stats.totalWorkingDays > 0
              ? Math.round(
                  (stats.totalDaysWorked / stats.totalWorkingDays) * 100,
                )
              : 0}
            %
          </div>
        </div>
        <div style={{ ...styles.statCard, background: "#94a3b8" }}>
          <div style={styles.statLabel}>Weekly Offs</div>
          <div style={styles.statValue}>{stats.totalHolidays}</div>
        </div>
        <div style={{ ...styles.statCard, background: "#fbbf24" }}>
          <div style={styles.statLabel}>Company Holidays</div>
          <div style={styles.statValue}>{stats.totalCompanyHolidays}</div>
        </div>
        <div style={{ ...styles.statCard, background: "#fb923c" }}>
          <div style={styles.statLabel}>Leaves</div>
          <div style={styles.statValue}>{stats.totalLeaves}</div>
        </div>
      </div>

      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#10b981" }} />
          <span>P - Present</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#f59e0b" }} />
          <span>Pa - Partial</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#ef4444" }} />
          <span>NP - Not Punched</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#94a3b8" }} />
          <span>H - Weekly Off</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#fbbf24" }} />
          <span>🎉 - Company Holiday</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#fb923c" }} />
          <span>L - Leave</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#fef9c3" }} />
          <span>HL - Half Leave</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#fef3c7" }} />
          <span>Leave Requested - Pending approval</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendColor, background: "#fee2e2" }} />
          <span>Leave Revoked - Worked despite leave</span>
        </div>
      </div>

      <div style={styles.gridWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.employeeHeader}>Employee</th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  ...styles.statsHeaderPresentCell,
                }}
              >
                Present
              </th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  ...styles.statsHeaderPartialCell,
                }}
              >
                Partial
              </th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  ...styles.statsHeaderAbsentCell,
                }}
              >
                Not Punched
              </th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  ...styles.statsHeaderHolidayCell,
                }}
              >
                Weekly Offs
              </th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  background: "#fbbf24",
                }}
              >
                Holidays
              </th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  ...styles.statsHeaderLeaveCell,
                }}
              >
                Leaves
              </th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  ...styles.statsHeaderHoursCell,
                }}
              >
                Total Hours
              </th>
              <th
                style={{
                  ...styles.statsHeaderCell,
                  ...styles.statsHeaderAvgCell,
                }}
              >
                Avg Hrs/Day
              </th>
              {days.map((day) => (
                <th key={day} style={styles.dayHeader}>
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employeeList.map((employee) => {
              const employeeName =
                typeof employee === "string" ? employee : employee.employeeName;
              const employeeData = calendarData[employeeName] || {};

              // employeeList entries are expected to always be the full
              // per-employee payload (monthlyAttendance, workingDays,
              // leaveHistories, holidays) as returned by /employee-monthly.
              // If this ever fires without monthlyAttendance, it means a
              // caller passed in a stripped-down employeeList — that's a
              // bug upstream (see ManageAttendance.jsx), not something to
              // silently paper over here, since a self-referential lookup
              // in the same array can never recover the missing fields.
              const fullEmployeeData = employee.monthlyAttendance
                ? employee
                : (console.warn(
                    `EnhancedAttendanceCalendar: employeeList entry for "${employeeName}" is missing monthlyAttendance/workingDays — weekly-off detection will be wrong for this row.`,
                  ),
                  employee);

              const empStats = fullEmployeeData
                ? calculateEmployeeDetailedStats(fullEmployeeData)
                : {
                    present: 0,
                    partial: 0,
                    absent: 0,
                    holidays: 0,
                    leaves: 0,
                    companyHolidays: 0,
                    totalHours: 0,
                    avgHours: 0,
                  };

              return (
                <tr key={employeeName}>
                  <td style={styles.employeeCell}>{employeeName}</td>
                  <td
                    style={{ ...styles.statsCell, ...styles.statsPresentCell }}
                  >
                    {empStats.present}
                  </td>
                  <td
                    style={{ ...styles.statsCell, ...styles.statsPartialCell }}
                  >
                    {empStats.partial}
                  </td>
                  <td
                    style={{ ...styles.statsCell, ...styles.statsAbsentCell }}
                  >
                    {empStats.absent}
                  </td>
                  <td
                    style={{ ...styles.statsCell, ...styles.statsHolidayCell }}
                  >
                    {empStats.holidays}
                  </td>
                  <td style={{ ...styles.statsCell, background: "#fef9c3" }}>
                    {empStats.companyHolidays}
                  </td>
                  <td style={{ ...styles.statsCell, ...styles.statsLeaveCell }}>
                    {empStats.leaves}
                  </td>
                  <td style={{ ...styles.statsCell, ...styles.statsHoursCell }}>
                    {Math.round(empStats.totalHours)}h
                  </td>
                  <td style={{ ...styles.statsCell, ...styles.statsAvgCell }}>
                    {empStats.avgHours.toFixed(1)}h
                  </td>
                  {days.map((day) => {
                    const dateKey = `${year}-${String(month + 1).padStart(
                      2,
                      "0",
                    )}-${String(day).padStart(2, "0")}`;
                    const record = employeeData[dateKey];

                    const date = new Date(year, month, day);
                    const rawLeave = fullEmployeeData?.leaveHistories?.find(
                      (l) => l.leaveDate === dateKey,
                    );
                    const leaveRevoked =
                      rawLeave && isRevokedLeave(rawLeave.leaveStatus);
                    const leavePending =
                      rawLeave &&
                      !leaveRevoked &&
                      isPendingLeave(rawLeave.leaveStatus);
                    // A revoked leave no longer blocks the day.
                    const leave = leaveRevoked ? null : rawLeave;
                    const isHol = fullEmployeeData?.workingDays
                      ? isHoliday(date, fullEmployeeData.workingDays)
                      : false;
                    const companyHoliday = isCompanyHoliday(dateKey);

                    const isOptionalHoliday =
                      companyHoliday &&
                      companyHoliday.holidayType === "Optional";

                    // DOJ comes back as a plain "YYYY-MM-DD" date, same
                    // shape as dateKey, so it can be compared as a string.
                    const employeeDoj = fullEmployeeData?.doj
                      ? fullEmployeeData.doj.substring(0, 10)
                      : null;
                    const isBeforeJoining = employeeDoj
                      ? dateKey < employeeDoj
                      : false;
                    const isJoiningDay = employeeDoj
                      ? dateKey === employeeDoj
                      : false;

                    const dayInfo = isBeforeJoining
                      ? { date: dateKey, dayType: "before-joining" }
                      : companyHoliday && !(isOptionalHoliday && leave)
                        ? {
                            date: dateKey,
                            dayType: "company-holiday",
                            isCompanyHoliday: true,
                            holidayName: companyHoliday.holidayName,
                            leaveRevoked,
                            isJoiningDay,
                          }
                        : leave
                          ? {
                              date: dateKey,
                              dayType: leavePending
                                ? "leave-requested"
                                : "leave",
                              leaveType: leave.leaveTypeName,
                              leaveDuration: leave.leaveDuration,
                              leaveStatus: leave.leaveStatus,
                              isJoiningDay,
                            }
                          : isHol
                            ? {
                                date: dateKey,
                                dayType: "holiday",
                                leaveRevoked,
                                isJoiningDay,
                              }
                            : { date: dateKey, leaveRevoked, isJoiningDay };

                    return (
                      <td
                        key={`${employeeName}-${day}`}
                        style={styles.cellWrapper}
                      >
                        <AttendanceCell
                          record={record}
                          dayInfo={dayInfo}
                          onClick={() => {}}
                          canDelete={canDelete}
                          onDelete={onDeleteRecord}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr style={styles.totalRow}>
              <td style={{ ...styles.employeeCell, ...styles.totalCell }}>
                Total
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {stats.totalPresent}
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {stats.totalPartial}
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {stats.totalAbsent}
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {stats.totalHolidays}
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {stats.totalCompanyHolidays}
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {stats.totalLeaves}
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {Math.round(stats.totalHoursWorked)}h
              </td>
              <td style={{ ...styles.statsCell, ...styles.totalCell }}>
                {stats.avgWorkingHours.toFixed(1)}h
              </td>
              {days.map((day) => (
                <td
                  key={`total-${day}`}
                  style={{ ...styles.cellWrapper, background: "#fef9c3" }}
                ></td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  container: {
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  monthNav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    padding: "5px",
    background: "#f9fafb",
    borderRadius: "8px",
  },
  monthTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: "#1f2937",
  },
  navButton: {
    padding: "8px",
    border: "none",
    background: "#fff",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    transition: "all 0.2s",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
  },
  statCard: {
    padding: "16px",
    borderRadius: "8px",
    color: "#fff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  statLabel: {
    fontSize: "12px",
    opacity: 0.9,
    marginBottom: "4px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 700,
  },
  legend: {
    marginBottom: "20px",
    padding: "16px",
    background: "#f9fafb",
    borderRadius: "8px",
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
  },
  legendColor: {
    width: "20px",
    height: "20px",
    borderRadius: "4px",
  },
  gridWrapper: {
    overflowX: "auto",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: "#fff",
  },
  employeeHeader: {
    padding: "12px 16px",
    background: "#f3f4f6",
    borderBottom: "2px solid #e5e7eb",
    borderRight: "2px solid #e5e7eb",
    fontWeight: 600,
    fontSize: "14px",
    color: "#374151",
    textAlign: "left",
    position: "sticky",
    left: 0,
    zIndex: 3,
    minWidth: "200px",
  },
  dayHeader: {
    padding: "12px 8px",
    background: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
    borderRight: "1px solid #e5e7eb",
    fontWeight: 600,
    fontSize: "14px",
    color: "#374151",
    textAlign: "center",
    minWidth: "50px",
  },
  employeeCell: {
    padding: "12px 16px",
    background: "#f9fafb",
    borderRight: "2px solid #e5e7eb",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 600,
    fontSize: "14px",
    color: "#1f2937",
    position: "sticky",
    left: 0,
    zIndex: 2,
    minWidth: "200px",
  },
  cellWrapper: {
    padding: 0,
    borderRight: "1px solid #e5e7eb",
    borderBottom: "1px solid #e5e7eb",
  },
  statsHeaderCell: {
    padding: "8px 6px",
    background: "#f3f4f6",
    borderBottom: "2px solid #e5e7eb",
    borderRight: "1px solid #e5e7eb",
    fontWeight: 600,
    fontSize: "11px",
    color: "#fff",
    textAlign: "center",
    whiteSpace: "nowrap",
    minWidth: "70px",
    position: "sticky",
    left: 0,
    zIndex: 2,
  },
  statsHeaderPresentCell: { background: "#10b981" },
  statsHeaderPartialCell: { background: "#f59e0b" },
  statsHeaderAbsentCell: { background: "#ef4444" },
  statsHeaderHolidayCell: { background: "#94a3b8" },
  statsHeaderLeaveCell: { background: "#fb923c" },
  statsHeaderHoursCell: { background: "#8b5cf6" },
  statsHeaderAvgCell: { background: "#06b6d4" },
  statsCell: {
    padding: "8px 6px",
    borderBottom: "1px solid #e5e7eb",
    borderRight: "1px solid #e5e7eb",
    fontSize: "12px",
    fontWeight: 500,
    color: "#1f2937",
    textAlign: "center",
    minWidth: "60px",
  },
  statsPresentCell: { background: "#ecfdf5", color: "#065f46" },
  statsPartialCell: { background: "#fef3c7", color: "#92400e" },
  statsAbsentCell: { background: "#fee2e2", color: "#991b1b" },
  statsHolidayCell: { background: "#f1f5f9", color: "#475569" },
  statsLeaveCell: { background: "#ffedd5", color: "#9a3412" },
  statsHoursCell: { background: "#f3e8ff", color: "#6b21a8" },
  statsAvgCell: { background: "#ecfeff", color: "#155e75" },
  totalRow: {
    fontWeight: 700,
    background: "#fef3c7",
  },
  totalCell: {
    fontWeight: 700,
    background: "#fef9c3",
    color: "#854d0e",
  },
};

const cellStyles = `
  .attendance-cell {
    position: relative;
    min-height: 60px;
    width: 100%;
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    cursor: default;
    gap: 2px;
    flex: 1;
    border: 2px solid transparent;
    border-radius: 4px;
  }

  .attendance-cell.has-record {
    cursor: pointer;
  }

  .attendance-cell.has-record:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 2;
  }



  .cell-status {
    font-size: 12px;
    font-weight: 700;
    text-align: center;
  }

  .cell-time {
    font-size: 9px;
    font-weight: 600;
    text-align: center;
  }

  .holiday-name {
    font-size: 10px;
    font-weight: 600;
    text-align: center;
    margin-top: 4px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cell-delete-btn {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fee2e2;
    border: 1px solid #fecaca;
    border-radius: 4px;
    color: #dc2626;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease;
    z-index: 3;
  }

  .attendance-cell:hover .cell-delete-btn {
    opacity: 1;
  }

  .cell-delete-btn:hover {
    background: #fecaca;
  }

  .cell-revoked-tag {
    font-size: 8px;
    font-weight: 700;
    text-align: center;
    color: #b91c1c;
    background: #fee2e2;
    border-radius: 3px;
    padding: 1px 4px;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }
`;

export default EnhancedAttendanceCalendar;
